import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    username: "", password: "", company_name: "", owner_contact: "", email: "", phone: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register({ ...form, role: "owner" });
      toast.success("Ownership account created");
      navigate("/");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background blueprint-grid flex items-center justify-center p-6 py-12">
      <form onSubmit={submit} className="w-full max-w-2xl bg-card border border-border p-8 lg:p-10 animate-fade-up" data-testid="register-form">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 bg-primary flex items-center justify-center">
            <HardHat size={22} weight="fill" className="text-white" />
          </div>
          <span className="font-heading font-black text-xl tracking-tighter">CONSTRUCT<span className="text-primary">HUB</span></span>
        </div>

        <div className="overline text-primary mb-2">Phase 1 · Project Setup</div>
        <h2 className="font-heading font-black text-3xl tracking-tighter mb-6">Create Ownership Account</h2>

        {error && (
          <div className="border border-destructive/50 bg-destructive/10 text-destructive text-sm px-4 py-3 mb-4" data-testid="register-error">
            {error}
          </div>
        )}

        <div className="overline text-muted-foreground mb-3">Ownership Information</div>
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <Field label="Username" required><Input value={form.username} onChange={set("username")} required data-testid="register-username-input" className="rounded-none h-11" /></Field>
          <Field label="Password" required><Input type="password" value={form.password} onChange={set("password")} required data-testid="register-password-input" className="rounded-none h-11" /></Field>
          <Field label="Company Name"><Input value={form.company_name} onChange={set("company_name")} data-testid="register-company-input" className="rounded-none h-11" /></Field>
          <Field label="Owner Contact"><Input value={form.owner_contact} onChange={set("owner_contact")} data-testid="register-contact-input" className="rounded-none h-11" /></Field>
          <Field label="Email" required><Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input" className="rounded-none h-11" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={set("phone")} data-testid="register-phone-input" className="rounded-none h-11" /></Field>
        </div>

        <Button type="submit" disabled={busy} data-testid="register-submit-button"
          className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white font-medium transition-transform duration-150 hover:-translate-y-[1px]">
          {busy ? "Creating…" : "Create Account"}
        </Button>

        <p className="text-sm text-muted-foreground text-center mt-4">
          Already registered?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="goto-login-link">Sign in</Link>
        </p>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="overline">{label}{required && <span className="text-primary ml-1">*</span>}</Label>
      {children}
    </div>
  );
}
